import { useMemo, useState } from "react";
import { q_seasonpass_seasonblast } from "@/queries/queries";
import { useQueries } from "@tanstack/react-query";
import { getv } from "@/utils/utils";
import { twMerge } from "tailwind-merge";

export const SeasonPass_SeasonBlast = () => {
  const [q] = useQueries({ queries: [q_seasonpass_seasonblast({}, {})] });
  const [copiedAddress, setCopiedAddress] = useState(null);

  const [seasonblast, vmap] = useMemo(() => {
    if (!q.dataUpdatedAt) return [null, null];
    let seasonblast = getv(q, "data.result.seasonblast", []);
    let vmap = getv(q, "data.result.vmap", {});
    return [seasonblast, vmap];
  }, [q.dataUpdatedAt]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateMobile = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = async (address) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error("Failed to copy address:", err);
    }
  };

  return (
    <div className="min-h-screen bg-r3bg relative">
      {/* Animated Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-5%] w-96 h-96 bg-r3lig/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-5%] w-96 h-96 bg-r3dark/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-[40%] right-[15%] w-64 h-64 bg-r3reg/15 rounded-full blur-2xl animate-pulse" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-digi font-bold text-transparent bg-clip-text bg-gradient-to-r from-r3lig via-r3reg to-r3dark mb-4">
            Season Blast Winners
          </h1>
          <p className="text-gray-400 text-lg">
            The following people are eligible for the Season Blast Tourney
          </p>
        </div>

        {/* Loading State */}
        {q.isLoading && (
          <div className="flex justify-center items-center py-20">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-2 border-r3lig/30 rounded-full"></div>
              <div className="absolute inset-0 border-2 border-transparent border-t-r3lig rounded-full animate-spin"></div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!q.isLoading && (!seasonblast || seasonblast.length === 0) && (
          <div className="text-center py-20">
            <div className="text-6xl mb-4 opacity-50">🎯</div>
            <h3 className="text-r3lig font-digi text-xl mb-2">
              No Season Blasts Yet
            </h3>
            <p className="text-gray-400">Check back later for updates</p>
          </div>
        )}

        {/* Season Blast List */}
        {seasonblast && seasonblast.length > 0 && (
          <div className="grid gap-4 md:gap-6">
            {seasonblast.map((blast, index) => {
              const vaultInfo = vmap[blast.vault] || {};
              const hasImage = vaultInfo.vault_img;
              const isCopied = copiedAddress === blast.vault;

              return (
                <div
                  key={index}
                  className={twMerge(
                    "group relative overflow-hidden",
                    "bg-gradient-to-br from-r3lig/10 via-r3reg/5 to-r3dark/10",
                    "backdrop-blur-xl border border-r3lig/20",
                    "rounded-2xl shadow-xl hover:shadow-2xl hover:shadow-r3lig/20",
                    "transition-all duration-500 hover:scale-[1.02] hover:border-r3lig/40",
                  )}
                >
                  {/* Animated gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-r3lig/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

                  {/* Mobile Layout */}
                  <div className="relative p-4 md:p-8 md:hidden">
                    <div className="flex items-start gap-3">
                      {/* Position Badge + Image */}
                      <div className="relative flex-shrink-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-r3lig/40 to-r3reg/40 rounded-full blur-xl scale-150 animate-pulse" />
                        <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-r3lig/30 shadow-xl">
                          {hasImage ? (
                            <img
                              src={vaultInfo.vault_img}
                              alt={vaultInfo.vault_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-r3dark to-r3reg flex items-center justify-center">
                              <span className="text-2xl">🎲</span>
                            </div>
                          )}
                        </div>
                        {/* Position Badge */}
                        <div className="absolute -top-1 -right-1 w-7 h-7 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg border border-white/20">
                          <span className="text-white font-bold text-sm">
                            {index + 1}
                          </span>
                        </div>
                      </div>

                      {/* Vault Info */}
                      <div className="flex-1 min-w-0 space-y-2">
                        <h3 className="text-lg font-digi font-bold text-transparent bg-clip-text bg-gradient-to-r from-r3lig to-r3reg truncate">
                          {vaultInfo.vault_name || "Unknown Vault"}
                        </h3>

                        {/* Compact Tags */}
                        <div className="space-y-2">
                          {/* Vault Address - Clickable */}
                          <button
                            onClick={() => copyToClipboard(blast.vault)}
                            className={twMerge(
                              "flex items-center gap-2 bg-r3dark/30 hover:bg-r3reg/30 px-3 py-1.5 rounded-full transition-all duration-200 text-sm",
                              "border border-r3lig/20 hover:border-r3lig/40",
                              isCopied && "bg-green-500/20 border-green-500/40",
                            )}
                          >
                            <span className="text-gray-400 text-xs">
                              Vault:
                            </span>
                            <span className="font-mono text-r3lig text-xs">
                              {truncateAddress(blast.vault)}
                            </span>
                            {isCopied ? (
                              <span className="text-green-400 text-xs">✓</span>
                            ) : (
                              <span className="text-gray-500 text-xs">📋</span>
                            )}
                          </button>

                          {/* Date */}
                          <div className="flex items-center gap-2 bg-r3dark/30 px-3 py-1.5 rounded-full text-sm w-fit">
                            <span className="text-gray-400 text-xs">Date:</span>
                            <span className="text-white text-xs">
                              {formatDateMobile(blast.date)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Trophy Icon - Mobile */}
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <div className="absolute inset-0 bg-yellow-400/20 rounded-full blur-lg animate-pulse" />
                          <div
                            className="relative text-3xl animate-bounce"
                            style={{ animationDelay: `${index * 0.1}s` }}
                          >
                            🏆
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="relative p-6 md:p-8 hidden md:block">
                    <div className="flex flex-col md:flex-row items-center gap-6">
                      {/* Vault Image */}
                      <div className="relative flex-shrink-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-r3lig/40 to-r3reg/40 rounded-full blur-2xl scale-150 animate-pulse" />
                        <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-r3lig/30 shadow-2xl">
                          {hasImage ? (
                            <img
                              src={vaultInfo.vault_img}
                              alt={vaultInfo.vault_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-r3dark to-r3reg flex items-center justify-center">
                              <span className="text-4xl md:text-5xl">🎲</span>
                            </div>
                          )}
                        </div>
                        {/* Position Badge */}
                        <div className="absolute -top-2 -right-2 w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white/20">
                          <span className="text-white font-bold text-lg md:text-xl">
                            {index + 1}
                          </span>
                        </div>
                      </div>

                      {/* Vault Info */}
                      <div className="flex-1 text-center md:text-left space-y-3">
                        <h3 className="text-2xl md:text-3xl font-digi font-bold text-transparent bg-clip-text bg-gradient-to-r from-r3lig to-r3reg">
                          {vaultInfo.vault_name || "Unknown Vault"}
                        </h3>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                          {/* Vault Address - Clickable */}
                          <button
                            onClick={() => copyToClipboard(blast.vault)}
                            className={twMerge(
                              "flex items-center gap-2 bg-r3dark/30 hover:bg-r3reg/30 px-4 py-2 rounded-full transition-all duration-200",
                              "border border-r3lig/20 hover:border-r3lig/40",
                              isCopied && "bg-green-500/20 border-green-500/40",
                            )}
                          >
                            <span className="text-gray-400 text-sm">
                              Vault:
                            </span>
                            <span className="font-mono text-r3lig">
                              {truncateAddress(blast.vault)}
                            </span>
                            {isCopied ? (
                              <span className="text-green-400">✓</span>
                            ) : (
                              <span className="text-gray-500">📋</span>
                            )}
                          </button>

                          {/* Date */}
                          <div className="flex items-center gap-2 bg-r3dark/30 px-4 py-2 rounded-full">
                            <span className="text-gray-400 text-sm">Date:</span>
                            <span className="text-white">
                              {formatDate(blast.date)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Trophy Icon */}
                      <div className="hidden lg:block">
                        <div className="relative">
                          <div className="absolute inset-0 bg-yellow-400/30 rounded-full blur-2xl animate-pulse" />
                          <div
                            className="relative text-6xl animate-bounce"
                            style={{ animationDelay: `${index * 0.1}s` }}
                          >
                            🏆
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom gradient line */}
                  <div className="h-1 bg-gradient-to-r from-r3lig via-r3reg to-r3dark opacity-50" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
